import { FormField } from "@/components/elements/FormField";
import { FormSchemaProvider } from "@/components/elements/Form";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { red, text } from "@/theme/colors";
import { Box, FormLabel, Stack, Typography } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { FC, useEffect, useMemo, useRef } from "react";
import { FormProvider, UseFormReturn, useWatch } from "react-hook-form";
import { validator as yup } from "@/utils/validator";

export type TReportPeriodForm = {
  periodFrom: Dayjs | null;
  periodTo: Dayjs | null;
};

const REPORT_PERIOD_LABELS = { startLabel: "報告対象期間始日", endLabel: "報告対象期間了日" } as const;
// 出力対象は本日から1年以内のデータに限定。始日が1年より前だと出力不可。
const WITHIN_ONE_YEAR_MESSAGE = "報告対象期間始日は本日から1年以内の日付を入力してください。";

// 会社フォームの利用開始日／利用終了日と同じバリデーション構成に揃える。
// isSelection → 未選択時「選択してください」／isDayjs → 日付不正／前後関係（始日 < 了日）の相互バリデーション。
export const reportPeriodSchema = yup.object({
  periodFrom: yup
    .mixed<Dayjs>()
    .isSelection()
    .isDayjs()
    .label(REPORT_PERIOD_LABELS.startLabel)
    .isBeforeDateOfField("periodTo", REPORT_PERIOD_LABELS)
    // 本日から1年前（当日含む）以降のみ許可。picker の minDate と一致させる（手入力時のガード）。
    .test("within-one-year", WITHIN_ONE_YEAR_MESSAGE, (value) => {
      if (!dayjs.isDayjs(value)) return true;
      return !value.isBefore(dayjs().startOf("day").subtract(1, "year"), "day");
    })
    .required(),
  periodTo: yup
    .mixed<Dayjs>()
    .isSelection()
    .isDayjs()
    .isAfterDateOfField("periodFrom", REPORT_PERIOD_LABELS)
    // 未来日不可（手入力ガード）。picker の maxDate=本日 と一致させる。
    .isPastOrToday()
    .label(REPORT_PERIOD_LABELS.endLabel)
    .required(),
});

export const getReportPeriodDefaults = (): TReportPeriodForm => {
  const startOfPrevMonth = dayjs().subtract(1, "month").startOf("month");
  const endOfPrevMonth = dayjs().subtract(1, "month").endOf("month").startOf("day");
  return { periodFrom: startOfPrevMonth, periodTo: endOfPrevMonth };
};

type Props = {
  methods: UseFormReturn<TReportPeriodForm>;
};

export const ReportPeriodField: FC<Props> = ({ methods }) => {
  const periodFrom = useWatch({ control: methods.control, name: "periodFrom" });
  const periodTo = useWatch({ control: methods.control, name: "periodTo" });

  const todayRef = useRef(dayjs().startOf("day"));
  const oneYearAgo = useMemo(() => todayRef.current.subtract(1, "year"), []);

  const { trigger, formState } = methods;

  // 会社フォームと同じ相互再検証。始日が変わったら、エラー中の了日を再検証する。
  useEffect(() => {
    if (formState.errors?.periodTo) {
      trigger("periodTo");
    }
  }, [periodFrom]);

  // 了日が変わったら、エラー中の始日を再検証する。
  useEffect(() => {
    if (formState.errors?.periodFrom) {
      trigger("periodFrom");
    }
  }, [periodTo]);

  return (
    <FormProvider {...methods}>
      <FormSchemaProvider schema={reportPeriodSchema}>
        <Stack gap={0.5}>
          <FormLabel required sx={{ fontSize: "12px", color: red[700] }}>
            <Typography variant="cap12" color={text.secondary}>
              報告対象期間
            </Typography>
          </FormLabel>
          <Stack direction="row" alignItems="flex-start" gap={1}>
            <Box width={190}>
              <FormField control={methods.control} name="periodFrom" label="">
                <InputDatePicker
                  placeholder="選択してください"
                  minDate={oneYearAgo}
                  maxDate={todayRef.current}
                />
              </FormField>
            </Box>
            <Typography variant="body14" color={text.secondary} mt={1}>
              〜
            </Typography>
            <Box width={190}>
              <FormField control={methods.control} name="periodTo" label="">
                <InputDatePicker
                  placeholder="選択してください"
                  minDate={oneYearAgo}
                  maxDate={todayRef.current}
                />
              </FormField>
            </Box>
          </Stack>
        </Stack>
      </FormSchemaProvider>
    </FormProvider>
  );
};
